/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/naming-convention */
import type { IPoint, IPosition, ISize } from '@/version-types';
import {
    boundsIntersectsInfo,
    calcDimensionsMatrix,
    CENTER,
    createRotateMatrix,
    createTranslateMatrix,
    degreesToRadians,
    LEFT,
    makeBoundingBoxFromPoints,
    multiplyTransformMatrices,
    Point,
    resolveOrigin,
    sizeAfterTransform,
    TOP,
    transformPoint,
    type TBBox,
    type TCornerPoint,
    type TDegree,
    type TOriginX,
    type TOriginY,
} from '../../shared';

type TACoords = TCornerPoint;

export class EdgelessBoundingBox {
    declare private __aCoords: TACoords;

    private originX: TOriginX = LEFT;
    private originY: TOriginY = TOP;

    constructor(
        public left: number = 0,
        public top: number = 0,
        public width: number = 0,
        public height: number = 0,
        public angle: TDegree = 0,
        centerIsLeftTop = false,
    ) {
        if (!centerIsLeftTop && (left !== 0 || top !== 0 || width !== 0 || height !== 0 || angle !== 0)) {
            const newInfo = this.calcCenterIsLeftTopPosition({
                left,
                top,
                width,
                height,
                rotate: angle,
            });
            this.left = newInfo.left;
            this.top = newInfo.top;
        }
    }

    /**
     * 获取中心点为左上角的坐标
     * @param layerLeft - 图层左
     * @param layerTop - 图层上
     * @param layerWidth - 图层宽
     * @param layerHeight - 图层高
     * @param layerAngle - 图层旋转角度
     * @returns - 中心点为左上角的坐标
     */
    calcCenterIsLeftTopPosition(oldInfo: TBBox & { rotate: TDegree }): IPosition {
        // 计算旋转中心从中心点改为左上角时的位置补偿
        const { left, top, width, height, rotate: angle } = oldInfo;

        // 当旋转中心从中心改为左上角时，需要调整位置
        // 使用旋转矩阵计算新的 left 和 top
        const cos = Math.cos((angle * Math.PI) / 180);
        const sin = Math.sin((angle * Math.PI) / 180);

        // 计算中心点到左上角的向量
        const centerToLeftTopX = -width / 2;
        const centerToLeftTopY = -height / 2;

        // 应用旋转变换
        const rotatedX = centerToLeftTopX * cos - centerToLeftTopY * sin;
        const rotatedY = centerToLeftTopX * sin + centerToLeftTopY * cos;

        // 计算新的左上角位置
        const newLeft = left + width / 2 + rotatedX;
        const newTop = top + height / 2 + rotatedY;

        return {
            left: newLeft,
            top: newTop,
        };
    }

    /**
     * 获取边界矩形
     * @returns - 边界矩形
     */
    getBoundingRect(): TBBox {
        return makeBoundingBoxFromPoints(this.getCoords());
    }

    /**
     * 获取坐标
     * @returns - 坐标
     */
    getCoords(): Point[] {
        const { tl, tr, br, bl } = this.__aCoords || (this.__aCoords = this.__calcACoords());
        const coords = [tl, tr, br, bl];
        return coords;
    }

    /**
     * 设置坐标
     */
    setCoords(): void {
        this.__aCoords = this.__calcACoords();
    }

    /** 获取相交信息 */
    getIntersectsInfo(
        zoom: number,
        transform: ISize & { left?: number; top?: number },
        translate: IPoint,
        blindAreas?: { top?: number; right?: number; bottom?: number; left?: number },
    ) {
        const { top = 0, right = 0, bottom = 0, left = 0 } = blindAreas || {};
        const scale = zoom;
        const translateX = translate.x;
        const translateY = translate.y;
        const target = this.getBoundingRect();

        const dest = {
            left: (transform.left ?? 0) + left,
            top: (transform.top ?? 0) + top,
            width: transform.width - left - right,
            height: transform.height - top - bottom,
        };

        target.top = target.top * scale + translateY;
        target.left = target.left * scale + translateX;
        target.width *= scale;
        target.height *= scale;

        return boundsIntersectsInfo(target, dest);
    }

    private __translateToGivenOrigin(
        point: Point,
        fromOriginX: TOriginX,
        fromOriginY: TOriginY,
        toOriginX: TOriginX,
        toOriginY: TOriginY,
    ): Point {
        let x = point.x,
            y = point.y;
        const offsetX = resolveOrigin(toOriginX) - resolveOrigin(fromOriginX),
            offsetY = resolveOrigin(toOriginY) - resolveOrigin(fromOriginY);

        if (offsetX || offsetY) {
            const dim = this.__getTransformedDimensions();
            x += offsetX * dim.x;
            y += offsetY * dim.y;
        }

        return new Point(x, y);
    }

    private __translateToCenterPoint(point: Point, originX: TOriginX, originY: TOriginY): Point {
        if (originX === CENTER && originY === CENTER) {
            return point;
        }
        const p = this.__translateToGivenOrigin(point, originX, originY, CENTER, CENTER);
        if (this.angle) {
            return p.rotate(degreesToRadians(this.angle), point);
        }
        return p;
    }

    private __getRelativeCenterPoint(): Point {
        return this.__translateToCenterPoint(new Point(this.left, this.top), this.originX, this.originY);
    }

    private __getTransformedDimensions(options: any = {}): Point {
        const dimOptions = {
            width: this.width,
            height: this.height,
            ...options,
        };

        const dimX = dimOptions.width,
            dimY = dimOptions.height;

        return sizeAfterTransform(dimX, dimY, calcDimensionsMatrix(dimOptions));
    }

    private __calcACoords(): TCornerPoint {
        const rotateMatrix = createRotateMatrix({ angle: this.angle }),
            { x, y } = this.__getRelativeCenterPoint(),
            tMatrix = createTranslateMatrix(x, y),
            finalMatrix = multiplyTransformMatrices(tMatrix, rotateMatrix),
            dim = this.__getTransformedDimensions(),
            w = dim.x / 2,
            h = dim.y / 2;
        return {
            // corners
            tl: transformPoint({ x: -w, y: -h }, finalMatrix),
            tr: transformPoint({ x: w, y: -h }, finalMatrix),
            bl: transformPoint({ x: -w, y: h }, finalMatrix),
            br: transformPoint({ x: w, y: h }, finalMatrix),
        };
    }
}
